import { useEffect, useState } from "react";

import { newsFetch } from "../../../services/api/News";
import PageHeader from "../../Header";

import NewsCard, { NewsItem } from "./NewsCard";
import Pagination from "./Pagination";

interface ApiResponse {
  status: number;
  ok: boolean;

  data: {
    data: NewsItem[];

    success: boolean;
    status: number;

    meta: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
    };
  };
}

const NewsPage: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalNews, setTotalNews] = useState(0);

  const fetchNews = async (page = 1) => {
    try {
      setLoading(true);
      setError("");

      const response: ApiResponse = await newsFetch(`news?page=${page}`);

      const apiData = response.data;

      if (response.ok && apiData.success) {
        setNews(apiData.data || []);

        setCurrentPage(apiData.meta.current_page || 1);
        setTotalPages(apiData.meta.last_page || 1);
        setTotalNews(apiData.meta.total || 0);
      } else {
        setNews([]);
        setError("No news found.");
      }
    } catch (error) {
      console.error(error);

      setNews([]);
      setError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews(currentPage);
  }, [currentPage]);

  return (
    <div className="min-h-screen bg-[#f7f7f7]">
      {/* HEADER */}
      <PageHeader
        title="Blogs"
        paragraph="Explore our latest blogs, insights, stories, and updates."
        backgroundImage=""
        align="center"
      />

      {/* CONTENT */}
      <div className="max-w-7xl mx-auto px-4 py-14">
        {/* LOADING */}
        {loading && (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* ERROR */}
        {!loading && error && (
          <div className="bg-red-50 border border-red-200 text-red-500 rounded-2xl p-6 text-center">
            {error}
          </div>
        )}

        {/* EMPTY */}
        {!loading && !error && news.length === 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-10 text-center text-gray-500">
            No News Available
          </div>
        )}

        {/* NEWS */}
        {!loading && !error && news.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {news.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>

            {/* PAGINATION */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalNews}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default NewsPage;
